/**
 * Hook for loading and computing AI cost trends data
 */

import { useState, useEffect, useMemo } from 'react'
import type {
  AIModelPricingData,
  ChartDataPoint,
  ModelSummary,
  CostTrendsFilterState,
} from './types'

const DATA_URL = '/data/ai-cost-trends.json'

export function useCostData() {
  const [data, setData] = useState<AIModelPricingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true)
        const response = await fetch(DATA_URL)
        if (!response.ok) {
          throw new Error(`Failed to load data: ${response.status}`)
        }
        const json = await response.json()
        setData(json)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  return { data, loading, error }
}

/**
 * Transform raw data into chart-ready format
 */
export function useChartData(
  data: AIModelPricingData | null,
  filters: CostTrendsFilterState
) {
  return useMemo(() => {
    if (!data) return []

    const points: ChartDataPoint[] = []

    data.providers
      .filter(
        (p) =>
          filters.selectedProviders.length === 0 ||
          filters.selectedProviders.includes(p.id)
      )
      .forEach((provider) => {
        provider.models.forEach((model) => {
          model.priceHistory.forEach((price) => {
            points.push({
              date: price.date,
              timestamp: new Date(price.date).getTime(),
              modelId: model.id,
              modelName: model.name,
              providerId: provider.id,
              providerName: provider.name,
              providerColor: provider.color,
              inputPer1M: price.inputPer1M,
              outputPer1M: price.outputPer1M,
              source: price.source,
              notes: price.notes,
              label: price.label,
            })
          })
        })
      })

    // Sort by date
    points.sort((a, b) => a.timestamp - b.timestamp)

    return points
  }, [data, filters.selectedProviders])
}

/**
 * Compute model summaries with current prices and reductions
 */
export function useModelSummaries(
  data: AIModelPricingData | null,
  filters: CostTrendsFilterState
) {
  return useMemo(() => {
    if (!data) return []

    const summaries: ModelSummary[] = []

    data.providers
      .filter(
        (p) =>
          filters.selectedProviders.length === 0 ||
          filters.selectedProviders.includes(p.id)
      )
      .forEach((provider) => {
        provider.models.forEach((model) => {
          if (model.priceHistory.length === 0) return

          const sortedHistory = [...model.priceHistory].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          )

          const first = sortedHistory[0]
          const current = sortedHistory[sortedHistory.length - 1]

          // Calculate price reduction percentage (based on input price)
          const reduction =
            first.inputPer1M > 0
              ? ((first.inputPer1M - current.inputPer1M) / first.inputPer1M) *
                100
              : 0

          const dataPoints: ChartDataPoint[] = sortedHistory.map((price) => ({
            date: price.date,
            timestamp: new Date(price.date).getTime(),
            modelId: model.id,
            modelName: model.name,
            providerId: provider.id,
            providerName: provider.name,
            providerColor: provider.color,
            inputPer1M: price.inputPer1M,
            outputPer1M: price.outputPer1M,
            source: price.source,
            notes: price.notes,
            label: price.label,
          }))

          summaries.push({
            modelId: model.id,
            modelName: model.name,
            providerId: provider.id,
            providerName: provider.name,
            providerColor: provider.color,
            family: model.family,
            currentInputPer1M: current.inputPer1M,
            currentOutputPer1M: current.outputPer1M,
            lowestInputPer1M: Math.min(...sortedHistory.map((p) => p.inputPer1M)),
            lowestOutputPer1M: Math.min(...sortedHistory.map((p) => p.outputPer1M)),
            priceReduction: reduction,
            dataPoints,
          })
        })
      })

    // Sort by current input price (cheapest first)
    summaries.sort((a, b) => a.currentInputPer1M - b.currentInputPer1M)

    return summaries
  }, [data, filters.selectedProviders])
}

/**
 * Compute aggregate statistics based on selected providers
 * Stats are scoped to the current selection for meaningful comparisons
 */
interface ModelPriceInfo {
  name: string
  provider: string
  price: number
  date: string
}

export function useCostStats(
  data: AIModelPricingData | null,
  selectedProviders: string[] = []
) {
  return useMemo(() => {
    if (!data) {
      return {
        totalModels: 0,
        totalProviders: 0,
        cheapestModel: null as ModelPriceInfo | null,
        mostExpensiveModel: null as ModelPriceInfo | null,
        largestReduction: null as { model: string; reduction: number } | null,
        overallReduction: 0,
      }
    }

    // Filter providers based on selection (empty = all)
    const providersToUse = selectedProviders.length > 0
      ? data.providers.filter(p => selectedProviders.includes(p.id))
      : data.providers

    let totalModels = 0
    let cheapestInput = Infinity
    let mostExpensiveInput = 0
    let cheapestModel: ModelPriceInfo | null = null
    let mostExpensiveModel: ModelPriceInfo | null = null
    let largestReduction = 0
    let largestReductionModel = ''

    providersToUse.forEach((provider) => {
      provider.models.forEach((model) => {
        totalModels++

        if (model.priceHistory.length === 0) return

        const sortedHistory = [...model.priceHistory].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )

        const first = sortedHistory[0]
        const current = sortedHistory[sortedHistory.length - 1]

        // Find the lowest price point in this model's history
        const lowestPricePoint = sortedHistory
          .filter((p) => p.inputPer1M > 0)
          .reduce((min, p) => (p.inputPer1M < min.inputPer1M ? p : min), sortedHistory[0])

        // Track cheapest model ever (exclude free models for meaningful comparison)
        if (lowestPricePoint.inputPer1M > 0 && lowestPricePoint.inputPer1M < cheapestInput) {
          cheapestInput = lowestPricePoint.inputPer1M
          cheapestModel = {
            name: model.name,
            provider: provider.name,
            price: lowestPricePoint.inputPer1M,
            date: lowestPricePoint.date,
          }
        }

        // Track most expensive first price point (for cost reduction calc)
        if (first.inputPer1M > mostExpensiveInput) {
          mostExpensiveInput = first.inputPer1M
          mostExpensiveModel = {
            name: model.name,
            provider: provider.name,
            price: first.inputPer1M,
            date: first.date,
          }
        }

        // Track largest price reduction within a single model
        if (sortedHistory.length > 1 && first.inputPer1M > 0 && current.inputPer1M > 0) {
          const reduction =
            ((first.inputPer1M - current.inputPer1M) / first.inputPer1M) * 100
          if (reduction > largestReduction) {
            largestReduction = reduction
            largestReductionModel = model.name
          }
        }
      })
    })

    // Calculate overall reduction multiplier (most expensive launch -> cheapest ever)
    const finalCheapestModel = cheapestModel as ModelPriceInfo | null
    const finalMostExpensiveModel = mostExpensiveModel as ModelPriceInfo | null
    const overallReduction =
      finalMostExpensiveModel?.price && finalCheapestModel?.price && finalCheapestModel.price > 0
        ? finalMostExpensiveModel.price / finalCheapestModel.price
        : 0

    return {
      totalModels,
      totalProviders: providersToUse.length,
      cheapestModel: finalCheapestModel,
      mostExpensiveModel: finalMostExpensiveModel,
      largestReduction:
        largestReduction > 0
          ? { model: largestReductionModel, reduction: largestReduction }
          : null,
      overallReduction,
    }
  }, [data, selectedProviders])
}
